import { transformSearch } from "@/api/helper.ts";
import { queryClient } from "@/query-client.ts";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import userService from "../services/user-service";

export const userSearchSchema = z.object({
	search: z.string().optional(),
	status: z.string().optional(),
	include: z.string().optional(),
	unassigned_employee: z.boolean().optional(),
	email: z.string().optional(),
	page: z.number().optional(),
	per_page: z.number().optional(),
});

export const usersQueryOptions = (search?: z.infer<typeof userSearchSchema>, options?: { enabled?: boolean; cacheTime?: number }) => {
	return queryOptions({
		queryKey: ["users", search],
		queryFn: () => userService.list(transformSearch(search, ["search", "status", "unassigned_employee", "email", "role"])),
		...options,
	});
};

export const userSessionsQueryOptions = (userId: string) => {
	return queryOptions({
		queryKey: ["sessions"],
		queryFn: () => userService.getSesions(userId),
	});
};

export const userQueryOptions = (userId: string) => {
	return queryOptions({
		queryKey: ["users", userId],
		queryFn: () => userService.findById(userId),
	});
};

export const useUserCreateMutation = (userId?: string) => {
	return useMutation({
		mutationKey: ["users", userId],
		mutationFn: userService.create,
		meta: {
			successMessage: "User created successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};

export const useUserAssignRoleMutation = () => {
	return useMutation({
		mutationKey: ["users"],
		mutationFn: userService.assignRole,
		meta: {
			successMessage: "Role assigned successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};

export const useUserAssignBranchMutation = () => {
	return useMutation({
		mutationKey: ["users"],
		mutationFn: userService.assignBranch,
		meta: {
			successMessage: "Branch assigned successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};

export const useUserRemoveBranchMutation = () => {
	return useMutation({
		mutationKey: ["users"],
		mutationFn: userService.removeBranch,
		meta: {
			successMessage: "Branch remove successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};

export const useUseRemoveRoleMutation = () => {
	return useMutation({
		mutationKey: ["users"],
		mutationFn: userService.removeRole,
		meta: {
			successMessage: "Role remove successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};

export const useUserMutation = (userId?: string) => {
	return useMutation({
		mutationKey: ["users", userId],
		mutationFn: userService.updateById,
		meta: {
			successMessage: "User updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};

export const useUserEmailMutation = () => {
	return useMutation({
		mutationKey: ["users"],
		mutationFn: userService.updateByIdEmail,
		meta: {
			successMessage: "User updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};

export const useUserEnableMutation = () => {
	return useMutation({
		mutationKey: ["users"],
		mutationFn: userService.enable,
		meta: {
			successMessage: "User enabled successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};

export const useUserPinCodeMutation = () => {
	return useMutation({
		mutationKey: ["users"],
		mutationFn: userService.updatePinCode,
		meta: {
			successMessage: "Pin Code updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};

export const useUserDisableMutation = () => {
	return useMutation({
		mutationKey: ["users"],
		mutationFn: userService.disable,
		meta: {
			successMessage: "User disabled successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
	});
};
